import { Button } from "@tento-ui/components/button/button";
import { DOM_DEBOUNCE_BRIDGE, DOM_HOST, RenderPlugin } from "@tento-lona/sheets-ui";
import { Toast } from "@tento-ui/components/toast/toast";
import { CalendarIcon, CheckCircleFilledIcon, LonaIcons } from "@tento-ui/ui/icons";
import { Storybook } from "@tento-ui/ui/storybook";
import { NewRowOptionsModal } from "./new-row-options-modal";
import {
  Appearance,
  DebounceExecutors,
  LonaSdk,
  MemoryStorage,
  RequestErrorHandlers,
} from "@tento-lona";
import { InvalidationSink, SheetRegistries } from "@tento-lona/sheets";

/** An offline client for the stories: the modal drafts into its pending row. */
const storyClient = new LonaSdk.Client({
  storage: new MemoryStorage(),
  host: DOM_HOST,
  registries: SheetRegistries.BUILTINS,
  invalidation: InvalidationSink.create(),
  onSheetPromoted: null,
  baseUrl: null,
  accessToken: null,
  fetch: null,
  rowsOptions: (client) => ({
    backend: client.httpBackend(),
    calendarConfig: LonaSdk.Client.EMPTY_CALENDAR_CONFIG,
    taskListConfig: LonaSdk.Client.EMPTY_TASK_LIST_CONFIG,
    calendarPrefs: null,
  }),
  requestErrorHandler: RequestErrorHandlers.TRACE,
  debounceExecutor: DebounceExecutors.browser(DOM_DEBOUNCE_BRIDGE),
  appearance: Appearance.HEADLESS,
});

function createMockPreview(
  id: string,
  label: string,
  description: string,
  icon: string,
): RenderPlugin.Preview {
  return {
    id,
    label,
    description,
    previewImageUrl: "",
    icon: LonaIcons.$template(icon),
    isAlias: false,
  };
}

// Minimal mock plugin for storybook examples
const mockPlugin = {
  id: "mock",
  label: "Mock",
  makeRowOptions: null,
} as any;

export class NewRowOptionsModalStoryBook extends Storybook.Default {
  title(): string {
    return "New Row Options Modal";
  }

  examples(): Storybook.Example[] {
    const eventPreview = createMockPreview(
      "event",
      "Event",
      "Create a new calendar event with time and location",
      CalendarIcon,
    );

    const taskPreview = createMockPreview(
      "task",
      "Task",
      "Create a new task with due date and priority",
      CheckCircleFilledIcon,
    );

    return [
      {
        title: "Event Row Options",
        render: () => {
          return NewRowOptionsModal.makeWith(
            eventPreview,
            mockPlugin,
            storyClient.rows,
            (row) => {
              Toast.info(`Create ${row.descriptor.id} with timeScale: ${row.timescaleSetting}`);
            },
          );
        },
      },
      {
        title: "Task Row Options",
        render: () => {
          return NewRowOptionsModal.makeWith(
            taskPreview,
            mockPlugin,
            storyClient.rows,
            (row) => {
              Toast.info(`Create ${row.descriptor.id} with timeScale: ${row.timescaleSetting}`);
            },
          );
        },
      },
      {
        title: "Show Modal Button",
        render: () => {
          return Button.makeWithLight("Open Row Options Modal", {
            type: "primary",
            onClick: () => {
              NewRowOptionsModal.makeWith(
                eventPreview,
                mockPlugin,
                storyClient.rows,
                (row) => {
                  Toast.info(
                    `Create ${row.descriptor.id} with timeScale: ${row.timescaleSetting}`,
                  );
                },
              ).reveal();
            },
          });
        },
      },
    ];
  }
}
